import { Utils } from '@pacem/pacem-core';
import { VERTEX_BUFFER_INDEX_MESH_POSITION, UNIFORM_GROUP_INDEX_CAMERA, UNIFORM_BINDING_INDEX_CAMERA, UNIFORM_GROUP_INDEX_TRANSFORM, UNIFORM_BINDING_INDEX_TRANSFORM, STORAGE_GROUP_INDEX_LINE, WGSL_VERTEXOUTPUT_STRUCT_NAME } from '../constants';
const DefaultLineVertexOptions = {
    inputPositionLocation: VERTEX_BUFFER_INDEX_MESH_POSITION,
    outputInstanceIndexLocation: 0,
    outputVertexIndexLocation: 1,
    storagePositionsGroupIndex: STORAGE_GROUP_INDEX_LINE,
    storagePositionsBindingIndex: 0,
    uniformDimensionsGroupIndex: STORAGE_GROUP_INDEX_LINE,
    uniformDimensionsBindingIndex: 1,
    uniformViewGroupIndex: UNIFORM_GROUP_INDEX_CAMERA,
    uniformViewBindingIndex: UNIFORM_BINDING_INDEX_CAMERA,
    uniformWorldGroupIndex: UNIFORM_GROUP_INDEX_TRANSFORM,
    uniformWorldBindingIndex: UNIFORM_BINDING_INDEX_TRANSFORM,
};
function getVertexOutputStructDefinition(opts) {
    return `struct ${WGSL_VERTEXOUTPUT_STRUCT_NAME} {
    @builtin(position) position : vec4f,                                                // clip space position
    @location(${opts.outputInstanceIndexLocation}) @interpolate(flat) instance : u32,   // instance index
    @location(${opts.outputVertexIndexLocation}) @interpolate(flat) index : u32,        // vertex index
}`;
}
export function getDefaultLineVertexWgsl(options = DefaultLineVertexOptions) {
    const opts = Utils.extend({}, DefaultLineVertexOptions, options);
    return `${getVertexOutputStructDefinition(opts)}

struct Transforms {
    world: mat4x4f,
    normal: mat3x3f,
}

struct Camera {
    view: mat4x4f,
    projection: mat4x4f,
    position: vec3f
}

@group(${opts.uniformWorldGroupIndex}) @binding(${opts.uniformWorldBindingIndex}) var<uniform> transforms: Transforms;
@group(${opts.uniformViewGroupIndex}) @binding(${opts.uniformViewBindingIndex}) var<uniform> camera: Camera;
@group(${opts.storagePositionsGroupIndex}) @binding(${opts.storagePositionsBindingIndex}) var<storage, read> positions: array<f32>;

@vertex fn main(@builtin(instance_index) instance: u32,
        @builtin(vertex_index) index: u32) -> ${WGSL_VERTEXOUTPUT_STRUCT_NAME} {

    let x = positions[3u * index + 0u];
    let y = positions[3u * index + 1u];
    let z = positions[3u * index + 2u];
    let position4 = vec4f(x, y, z, 1.0);
    let worldTransform = transforms.world;
    let viewProjectionTransform = camera.projection;
    let worldViewProjectionTransform = viewProjectionTransform * worldTransform;
    let worldPosition = (worldTransform * position4).xyz;
    return ${WGSL_VERTEXOUTPUT_STRUCT_NAME}(
        worldViewProjectionTransform * position4,
        instance,
        index);
}`;
}
export function getFatLineVertexWgsl(options = DefaultLineVertexOptions) {
    const opts = Utils.extend({}, DefaultLineVertexOptions, options);
    return `${getVertexOutputStructDefinition(opts)}

struct Transforms {
    world: mat4x4f,
    normal: mat3x3f,
}

struct Camera {
    view: mat4x4f,
    projection: mat4x4f,
    position: vec3f
}

struct Pixels {
    viewportWidth: f32,
    viewportHeight: f32,
    halfLineWidth: f32
}

@group(${opts.uniformWorldGroupIndex}) @binding(${opts.uniformWorldBindingIndex}) var<uniform> transforms: Transforms;
@group(${opts.uniformViewGroupIndex}) @binding(${opts.uniformViewBindingIndex}) var<uniform> camera: Camera;
@group(${opts.storagePositionsGroupIndex}) @binding(${opts.storagePositionsBindingIndex}) var<storage, read> positions: array<f32>;
@group(${opts.uniformDimensionsGroupIndex}) @binding(${opts.uniformDimensionsBindingIndex}) var<uniform> dimensions: Pixels;

fn computeClipPosition(index: u32) -> vec4f {
    let x = positions[3u * index + 0u];
    let y = positions[3u * index + 1u];
    let z = positions[3u * index + 2u];
    let position4 = vec4f(x, y, z, 1.0);
    let worldTransform = transforms.world;
    let viewProjectionTransform = camera.projection;
    let worldViewProjectionTransform = viewProjectionTransform * worldTransform;
    return worldViewProjectionTransform * position4;
}

fn computeOffsetPosition(p0: vec4f, p1: vec4f, index: u32) -> vec4f {

   let viewportSize = vec2f(dimensions.viewportWidth, dimensions.viewportHeight);
   let normalizedSize = normalize(viewportSize);
   let halfThickness = dimensions.halfLineWidth / dimensions.viewportWidth;
   let p0ndc = p0.xyz / p0.w;
   let p1ndc = p1.xyz / p1.w;
   let dir = normalize((p1ndc - p0ndc).xy);
   let offsetX = halfThickness * 2.0 / normalizedSize.x;
   let offsetY = halfThickness * 2.0 / normalizedSize.y;

   // perp
   let perp0 = vec2f(-dir.y * offsetX, dir.x * offsetY);
   let perp1 = perp0;

    /*

        position0-0 -----*----- position0-1
                    |\\       |
                    | \\      |
                    |  \\     |
                    |   \\    |
                    |    \\   |
                    |     \\  |
                    |      \\ |
                    |       \\|
        position1-0 -----*----- position1-1

        indexes:
        ------------
        p0-0 -> 0, 3
        p0-1 -> 5
        p1-0 -> 1
        p1-1 -> 2, 4
    */

   var outPos: vec2f;
   var w = p0.w;
   var z = p0.z;
   if (index == 0 || index == 3){
       outPos = p0ndc.xy - perp0;
   }
   else if (index == 1){
       outPos = p1ndc.xy - perp1;
       w = p1.w;
       z = p1.z;
   }
   else if (index == 5){
       outPos = p0ndc.xy + perp0;
   }
   else { //if (index == 2 || index == 4)
       outPos = p1ndc.xy + perp1;
       w = p1.w;
       z = p1.z;
   }

   // return vec4f(outPos , 0, 1.0);
   return vec4f(outPos * w, z, w);
}

@vertex fn main(@builtin(instance_index) instance: u32,
        @builtin(vertex_index) index: u32) -> ${WGSL_VERTEXOUTPUT_STRUCT_NAME} {

    // vertex index starting at 1
    let position0 = computeClipPosition(instance);
    let position1 = computeClipPosition(instance + 1);

    // apply offset
    let position = computeOffsetPosition(position0, position1, index);

    return ${WGSL_VERTEXOUTPUT_STRUCT_NAME}(
        position,
        instance,
        index
    );
}`;
}
